import {
  FILE_TIMEOUT_MS,
  baseApi,
  resourceListTag,
  resourceScopeTag,
  resourceTag,
  type TransportRequest,
} from '@krp/client-runtime'

import { decodeEnvelope, requestEnvelope, withMeta, type NativeRead } from '../../pricing/api/envelope'
import type {
  CommandAttempt,
  ImportProfile,
  ImportedFile,
  InvoiceColumnRole,
  ParsedInvoice,
  ValidationReport,
} from '../model/types'
import { decodeFile, decodeParsedInvoice, decodeProfile, decodeProfiles, decodeReport } from './contracts'
import { releaseUpload, takeUpload } from './uploadRegistry'
import { ONEC_IMPORT_URLS } from './urls'

/**
 * Endpoints импорта накладных 1С в ОБЩЕМ `baseApi`: имена `onecImport*`, rid —
 * аргумент каждого запроса и часть каждого тега. Команды несут `operationId` и
 * ключ идемпотентности, созданные один раз на намерение человека.
 */
export const ONEC_IMPORT_TAG = 'OnecImport' as const

/**
 * Загрузка файла — multipart: поле `formData` общего transport (RP-AU1a), без
 * JSON-тела и своего `Content-Type`.
 */
export interface MultipartRequest extends TransportRequest {
  formData: FormData
}

export type ImportDownload = 'original' | 'report-csv'

/**
 * Запрос файла накладной или CSV-отчёта: идёт командой `requestFile` мимо кэша
 * RTK — Blob в Redux не кладётся.
 */
export function buildDownloadRequest(kind: ImportDownload, resourceId: string, fileId: string): TransportRequest {
  return {
    url: kind === 'original' ? ONEC_IMPORT_URLS.download(resourceId, fileId) : ONEC_IMPORT_URLS.reportCsv(resourceId, fileId),
  }
}

export interface ResourceArgs {
  resourceId: string
}

export interface ProfilesArgs {
  resourceId: string
  includeInactive: boolean
  supplier: string | null
}

export interface FileArgs {
  resourceId: string
  fileId: string
}

interface CommandBase extends CommandAttempt {
  resourceId: string
  configurationVersion: string
}

export interface UploadArgs extends CommandBase {
  kind: 'upload'
  /** Токен файла в `uploadRegistry`: сам `File` в Redux не кладётся. */
  uploadToken: string
  sheet: string | null
  headerRow: number | null
  profileId: string | null
  supplier: string | null
}

export interface MappingData {
  columns: readonly { columnIndex: number; role: InvoiceColumnRole }[]
  sheetName: string | null
  headerRowIndex: number | null
  saveAsProfile: boolean
  profileId: string | null
  profileName: string | null
  supplierName: string | null
  defaultUnit: string | null
  defaultVatRate: string | null
  priceIncludesVat: boolean
  quantityInPackages: boolean
}

export interface MappingArgs extends CommandBase {
  kind: 'mapping'
  fileId: string
  data: MappingData
}

export type ParseArgs = UploadArgs | MappingArgs

export interface ValidateArgs extends CommandBase {
  fileId: string
}

export interface ProfileData {
  name: string
  columns: readonly {
    role: InvoiceColumnRole
    headerText: string | null
    columnIndex: number | null
    isRequiredOverride: boolean | null
  }[]
  supplierName: string | null
  sheetNameHint: string | null
  headerRowIndex: number | null
  defaultUnit: string | null
  defaultVatRate: string | null
  priceIncludesVat: boolean
  quantityInPackages: boolean
}

export interface CreateProfileArgs extends CommandBase {
  data: ProfileData
}

export interface UpdateProfileArgs extends CommandBase {
  profileId: string
  data: ProfileData
}

export interface DeleteProfileArgs extends CommandBase {
  profileId: string
}

type Wire<T> = { data: T; configurationVersion: string }

function finish<T>(response: unknown, meta: unknown): NativeRead<T> {
  return withMeta(response as Wire<T>, meta)
}

const tag = (resourceId: string, id: string) => resourceTag(ONEC_IMPORT_TAG, resourceId, id)

/** Тело multipart по договору: ровно один `file` и три поля команды. */
export function buildUploadRequest(args: UploadArgs): MultipartRequest {
  const file = takeUpload(args.uploadToken)
  if (file === undefined) throw new Error('Файл накладной больше не доступен: выберите его заново')
  const formData = new FormData()
  formData.append('file', file, file.name)
  formData.append('operationId', args.operationId)
  formData.append('idempotencyKey', args.idempotencyKey)
  formData.append('expectedConfigurationVersion', args.configurationVersion)
  return {
    url: ONEC_IMPORT_URLS.invoice(args.resourceId),
    method: 'POST',
    params: { sheet: args.sheet, headerRow: args.headerRow, profileId: args.profileId, supplier: args.supplier },
    formData,
    timeoutMs: FILE_TIMEOUT_MS,
    decode: (raw) => decodeEnvelope(raw, args.resourceId, decodeParsedInvoice),
  }
}

const command = (args: CommandBase, action: string) => ({
  lane: 'Import',
  action,
  configurationVersion: args.configurationVersion,
  command: { operationId: args.operationId, idempotencyKey: args.idempotencyKey },
})

export const importApi = baseApi.enhanceEndpoints({ addTagTypes: [ONEC_IMPORT_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    onecImportProfiles: build.query<NativeRead<readonly ImportProfile[]>, ProfilesArgs>({
      query: ({ resourceId, includeInactive, supplier }) => ({
        url: ONEC_IMPORT_URLS.profiles(resourceId),
        params: { includeInactive, supplier },
        decode: (raw) => decodeEnvelope(raw, resourceId, decodeProfiles),
      }),
      transformResponse: (response: unknown, meta) => finish<readonly ImportProfile[]>(response, meta),
      providesTags: (_result, _error, { resourceId }) => [
        resourceListTag(ONEC_IMPORT_TAG, resourceId),
        resourceScopeTag(resourceId),
      ],
    }),
    onecImportFile: build.query<NativeRead<ImportedFile>, FileArgs>({
      query: ({ resourceId, fileId }) => ({
        url: ONEC_IMPORT_URLS.file(resourceId, fileId),
        decode: (raw) => decodeEnvelope(raw, resourceId, decodeFile),
      }),
      transformResponse: (response: unknown, meta) => finish<ImportedFile>(response, meta),
      providesTags: (_result, _error, { resourceId, fileId }) => [
        tag(resourceId, `FILE:${fileId}`),
        resourceScopeTag(resourceId),
      ],
    }),
    onecImportReport: build.query<NativeRead<ValidationReport>, FileArgs>({
      query: ({ resourceId, fileId }) => ({
        url: ONEC_IMPORT_URLS.report(resourceId, fileId),
        decode: (raw) => decodeEnvelope(raw, resourceId, decodeReport),
      }),
      transformResponse: (response: unknown, meta) => finish<ValidationReport>(response, meta),
      providesTags: (_result, _error, { resourceId, fileId }) => [
        tag(resourceId, `REPORT:${fileId}`),
        resourceScopeTag(resourceId),
      ],
    }),
    /** Загрузка файла или применение разметки: оба отвечают разобранной накладной. */
    onecImportParse: build.mutation<NativeRead<ParsedInvoice>, ParseArgs>({
      query: (args) =>
        args.kind === 'upload'
          ? buildUploadRequest(args)
          : {
              url: ONEC_IMPORT_URLS.mapping(args.resourceId, args.fileId),
              method: 'POST',
              body: requestEnvelope(command(args, 'ApplyMapping'), args.data),
              decode: (raw) => decodeEnvelope(raw, args.resourceId, decodeParsedInvoice),
            },
      transformResponse: (response: unknown, meta) => finish<ParsedInvoice>(response, meta),
      async onQueryStarted(args, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch {
          // Отказ показывает экран из состояния мутации; здесь — только уборка файла.
        } finally {
          if (args.kind === 'upload') releaseUpload(args.uploadToken)
        }
      },
      invalidatesTags: (result, _error, args) => {
        const fileId = args.kind === 'mapping' ? args.fileId : result?.data.fileId
        return fileId === undefined
          ? []
          : [tag(args.resourceId, `FILE:${fileId}`), tag(args.resourceId, `REPORT:${fileId}`)]
      },
    }),
    onecImportValidate: build.mutation<NativeRead<ValidationReport>, ValidateArgs>({
      query: (args) => ({
        url: ONEC_IMPORT_URLS.validate(args.resourceId, args.fileId),
        method: 'POST',
        body: requestEnvelope(command(args, 'Validate'), null),
        decode: (raw) => decodeEnvelope(raw, args.resourceId, decodeReport),
      }),
      transformResponse: (response: unknown, meta) => finish<ValidationReport>(response, meta),
      invalidatesTags: (_result, _error, { resourceId, fileId }) => [
        tag(resourceId, `FILE:${fileId}`),
        tag(resourceId, `REPORT:${fileId}`),
      ],
    }),
    onecImportCreateProfile: build.mutation<NativeRead<ImportProfile>, CreateProfileArgs>({
      query: (args) => ({
        url: ONEC_IMPORT_URLS.profiles(args.resourceId),
        method: 'POST',
        body: requestEnvelope(command(args, 'CreateProfile'), args.data),
        decode: (raw) => decodeEnvelope(raw, args.resourceId, decodeProfile),
      }),
      transformResponse: (response: unknown, meta) => finish<ImportProfile>(response, meta),
      invalidatesTags: (_result, _error, { resourceId }) => [resourceListTag(ONEC_IMPORT_TAG, resourceId)],
    }),
    onecImportUpdateProfile: build.mutation<NativeRead<ImportProfile>, UpdateProfileArgs>({
      query: (args) => ({
        url: ONEC_IMPORT_URLS.profile(args.resourceId, args.profileId),
        method: 'PUT',
        body: requestEnvelope(command(args, 'UpdateProfile'), args.data),
        decode: (raw) => decodeEnvelope(raw, args.resourceId, decodeProfile),
      }),
      transformResponse: (response: unknown, meta) => finish<ImportProfile>(response, meta),
      invalidatesTags: (_result, _error, { resourceId }) => [resourceListTag(ONEC_IMPORT_TAG, resourceId)],
    }),
    onecImportDeleteProfile: build.mutation<NativeRead<null>, DeleteProfileArgs>({
      query: (args) => ({
        url: ONEC_IMPORT_URLS.profile(args.resourceId, args.profileId),
        method: 'DELETE',
        body: requestEnvelope(command(args, 'DeleteProfile'), null),
        decode: (raw) => decodeEnvelope(raw, args.resourceId, () => null),
      }),
      transformResponse: (response: unknown, meta) => finish<null>(response, meta),
      invalidatesTags: (_result, _error, { resourceId }) => [resourceListTag(ONEC_IMPORT_TAG, resourceId)],
    }),
  }),
})

export const {
  onecImportProfiles,
  onecImportFile,
  onecImportReport,
  onecImportParse,
  onecImportValidate,
  onecImportCreateProfile,
  onecImportUpdateProfile,
  onecImportDeleteProfile,
} = importApi.endpoints
